import { useEffect, useRef, useState } from "react";

import { ProviderHooks } from "@/src/hook";
import { CMSLogic } from "@/src/logic";
import { StrapiCollectionType } from "@/src/type";

interface DynamicPageAlternateProps {
  page: StrapiCollectionType.DynamicPage;
  refreshId: string;
}

function DynamicPageAlternate({ page, refreshId }: DynamicPageAlternateProps) {
  const app = ProviderHooks.useApp();
  const prevScrollY = useRef<number>(0);
  const lastSection = useRef<number>(0);
  const [updateHeader, setUpdateHeader] = useState<number>(0);
  const [lastUpdateHeader, setLastUpdateHeader] = useState<number>(0);
  const [lastRefreshId, setLastRefreshId] = useState<string>("");

  /**
   * when the header observer is enabled for the alternate layout
   * we need to check the last section that is visible in the viewport
   * and update the header accordingly
   */
  useEffect(() => {
    if (page.layout === "alternate" && page?.headerObserver) {
      const handleScroll = () => {
        const scrollUp = window.scrollY > prevScrollY.current;
        prevScrollY.current = window.scrollY;
        const swiperSlides = document.querySelectorAll("div[class*=swiper-slide]");

        let checkLastSection = lastSection.current;
        swiperSlides.forEach((slide) => {
          const rect = slide.getBoundingClientRect();
          if (rect.top < 30 && rect.top > -320 && slide.id) {
            const sectionId = parseInt(slide.id.split("_")[1], 10);

            if (scrollUp && sectionId > checkLastSection) {
              checkLastSection = sectionId;
              //console.log(`${checkLastSection} - ${scrollUp ? "up" : "down"}`, rect.top);
            } else if (!scrollUp && sectionId < checkLastSection) {
              checkLastSection = sectionId;
              //console.log(`${checkLastSection} - ${scrollUp ? "up" : "down"}`, rect.top);
            }
          }
        });

        if (checkLastSection !== lastSection.current) {
          //console.log("triggering", checkLastSection);
          lastSection.current = checkLastSection;
          setUpdateHeader(checkLastSection);
        }
      };

      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [page]);

  /**
   * once the header observer triggers a change, update the header with the new section
   */
  useEffect(() => {
    if (lastUpdateHeader === updateHeader && lastRefreshId === refreshId) {
      return;
    }

    if (!Array.isArray(page.dynamic_page_sections?.data) || (page.dynamic_page_sections?.data ?? []).length === 0) {
      return;
    }

    //set header properties
    const currentSection = page.dynamic_page_sections?.data[updateHeader];
    const backgroundColor = CMSLogic.colorSelector(currentSection?.attributes?.background?.color) ?? "transparent";
    app.setHeader({
      toChangeHeader: currentSection?.attributes?.header,
      toChangeBackgroundColor: backgroundColor,
    });
    setLastUpdateHeader(updateHeader);
    setLastRefreshId(refreshId);
  }, [lastUpdateHeader, updateHeader, app, page, refreshId, lastRefreshId]);

  return <></>;
}

export default DynamicPageAlternate;
